import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Check, X, Clock, Truck, UserCog } from 'lucide-react';
import { driverRequestApi, DriverRequest } from '../../services/driverRequestApi';

export function ApprovalBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState<DriverRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchCount = useCallback(async () => {
    try {
      const c = await driverRequestApi.getPendingCount();
      setCount(c);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      const insideButton = panelRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideButton && !insideDropdown) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const openPanel = async () => {
    const next = !open;
    if (next && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const panelWidth = 384; // w-96
      const margin = 8;
      const maxLeft = window.innerWidth - panelWidth - margin;
      const left = Math.min(Math.max(rect.left, margin), maxLeft);
      setPanelPos({ top: rect.bottom + margin, left });
    }
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const list = await driverRequestApi.listPending();
        setRequests(list);
      } catch { /* ignore */ }
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await driverRequestApi.approve(id);
      setRequests(prev => prev.filter(r => r.id !== id));
      setCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const handleReject = async (id: number) => {
    try {
      await driverRequestApi.reject(id);
      setRequests(prev => prev.filter(r => r.id !== id));
      setCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'type_change': return <UserCog className="w-4 h-4 text-blue-500" />;
      case 'vehicle_associate':
      case 'vehicle_disassociate': return <Truck className="w-4 h-4 text-orange-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={openPanel}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        title="Approval Requests"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && panelPos && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: panelPos.top, left: panelPos.left }}
          className="w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-[1000] overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 text-sm">Approval Requests</h3>
            <span className="text-xs text-slate-400">{count} pending</span>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="py-8 text-center text-slate-400 text-sm">Loading...</div>
            )}

            {!loading && requests.length === 0 && (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">No pending requests</p>
              </div>
            )}

            {!loading && requests.map(req => (
              <div key={req.id} className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{getIcon(req.request_type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{req.driver_name || 'Unknown Driver'}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{driverRequestApi.formatDescription(req)}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{timeAgo(req.created_at)}</p>
                  </div>
                  <div className="flex gap-1.5 ml-2 flex-shrink-0">
                    <button
                      onClick={() => handleApprove(req.id)}
                      className="p-1.5 rounded-md bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
                      title="Approve"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReject(req.id)}
                      className="p-1.5 rounded-md bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                      title="Reject"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
